import {
  Component,
  HostListener,
  Input,
  ElementRef,
  ChangeDetectorRef,
  OnDestroy,
  OnChanges,
  AfterViewInit,
  Output,
  EventEmitter,
  OnInit,
  forwardRef,
  AfterViewChecked,
  AfterContentChecked,
  SimpleChanges,
  Inject,
} from '@angular/core';
import { Subject, Subscription, debounceTime, distinctUntilChanged, map, take } from 'rxjs';
import { ControlValueAccessor, FormControl, NG_VALUE_ACCESSOR } from '@angular/forms';
import { FormFieldControlDirective } from '../form-field/directives';
import { DropdownAnimation } from './dropdown.animation';
import { IOption } from './option.interface';
import { DICTIONARY_PROVIDER, IDictionaryProviderService } from '@common/dictionaries';

@Component({
  selector: 'app-select',
  templateUrl: './select.component.html',
  styleUrls: ['./select.component.scss'],
  animations: [DropdownAnimation],
  providers: [
    { provide: FormFieldControlDirective, useExisting: SelectComponent },
    // { provide: NG_VALUE_ACCESSOR, multi: true, useExisting: SelectComponent },
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => SelectComponent),
      multi: true,
    },
  ],
})
export class SelectComponent
  implements
    FormFieldControlDirective<string | number | number[] | null>,
    ControlValueAccessor,
    OnInit,
    AfterViewInit,
    AfterViewChecked,
    AfterContentChecked,
    OnChanges,
    OnDestroy
{
  @Input() options?: IOption<number | string>[] | undefined | null;
  @Input() placeholder!: string;
  @Input() enumCode: string | null = null;
  /** возможность включение режима выбора(редактирования) значения из enum на режим только просмотра */
  @Input() isEditMode: boolean = true;
  @Input()
  get value(): string | number | number[] | null {
    return this._value;
  }

  set value(newValue: any) {
    this._value = newValue;
    this.model = this.renderValue();
  }

  model: string | number = '';
  showData: IOption<number | string>[] | undefined | null;
  private _value: any = undefined;
  focused: boolean = false;
  controlType: string = 'select';
  touched: boolean = false;
  stateChanges: Subject<void> = new Subject<void>();

  @Input() disabled: boolean = false;
  @Output() onChange: EventEmitter<any> = new EventEmitter<any>();

  _onChange: (value: any) => void = () => {};
  _onTouched = () => {};

  constructor(
    @Inject(DICTIONARY_PROVIDER) private service: IDictionaryProviderService,
    private elementRef: ElementRef<HTMLDivElement>,
    private changeDetectorRef: ChangeDetectorRef,
  ) {}

  ngAfterViewChecked(): void {}

  ngAfterContentChecked(): void {}

  ngAfterContentInit(): void {}

  ngOnInit(): void {}

  ngAfterViewInit(): void {
    this.stateChanges.next();
  }

  ngOnChanges({ options, enumCode }: SimpleChanges): void {
    this.stateChanges.next();
    if (options) {
      this.showData = this.options;
      this.model = this.renderValue();
    }
    if (enumCode) {
      this.service
        .getDictionaryValuesByType(this.enumCode!)
        .pipe(
          map((vals) => {
            return vals.map((item) => ({ id: item.code, name: item.name }));
          }),
          take(1),
        )
        .subscribe((opt) => {
          this.showData = opt;
          this.options = opt;
          this.model = this.renderValue();
          this.changeDetectorRef.markForCheck();
        });
    }
  }

  ngOnDestroy(): void {
    this.stateChanges.complete();
  }

  writeValue(value: any): void {
    this.value = value;
    this.model = this.renderValue();
  }

  registerOnChange(fn: (value: any) => void): void {
    this._onChange = fn;
  }

  registerOnTouched(fn: () => {}): void {
    this._onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
    this.changeDetectorRef.markForCheck();
    this.stateChanges.next();
  }

  toggle(): void {
    if (this.disabled) return; // read-only: список не открываем
    this.focused = !this.focused;
    this._onTouched();
    this.touched = true;
    this.stateChanges.next();
  }

  renderValue(): string {
    const option = this.showData?.find((option) => option.id === this.value);
    return option !== undefined && option !== null ? option.name : '';
  }

  select(value: string | number): void {
    if (this.disabled) return; // read-only: выбор запрещён
    this.onChange.emit(value);
    this.writeValue(value);
    this._onChange(this.value);
    this._onTouched();
    this.focused = false;
    this.stateChanges.next();
    this.showData = this.options;
  }

  search(event: any): void {
    if (this.disabled) return; // read-only: ввод/поиск запрещён
    if (this.options) {
      this.showData = this.options.filter((item: any) => {
        return item.name.toLowerCase().includes(event.toLowerCase());
      });
      // NOTE: автооткрытие выпадающего списка при поиске
      this.focused = this.showData.length > 0;
    }
  }

  @HostListener('document:click', ['$event.target'])
  outsideClick(targetElement: Node | null): void {
    if (this.focused && !this.elementRef.nativeElement.contains(targetElement)) {
      this.focused = false;
      this.model = this.renderValue();
      this.showData = this.options;
      this.stateChanges.next();
    }
  }
}
